import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermission } from "@/contexts/RoleAccessContext";
import { Loader2 } from "lucide-react";
import { getSubscriptionStatus } from "@/services/subscriptionService";

/* -------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------- */
const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/* -------------------------------------------------- */
/* Protected Route Component */
/* -------------------------------------------------- */
const ProtectedRoute = ({
  children,
  requireRole = null,
  allowedRoles = [],
  permission = null, // { feature: string | string[], actions: string | string[] }
}) => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { can, loading: permissionLoading } = usePermission();
  const location = useLocation();

  const [subChecking, setSubChecking] = useState(false);
  const [subRequiresPayment, setSubRequiresPayment] = useState(false);

  /* -------------------------------------------------- */
  /* Subscription Check (Admin Only)                    */
  /* -------------------------------------------------- */
  useEffect(() => {
    const checkStatus = async () => {
      if (!isAuthenticated || !user || user.role !== "admin") return;

      try {
        setSubChecking(true);
        const res = await getSubscriptionStatus();

        if (res?.requires_payment) {
          setSubRequiresPayment(true);
        }
      } catch (error) {
        console.error("Subscription check failed:", error);
      } finally {
        setSubChecking(false);
      }
    };

    checkStatus();
  }, [isAuthenticated, user]);

  /* -------------------------------------------------- */
  /* GLOBAL LOADING BLOCK                              */
  /* -------------------------------------------------- */
  if (authLoading || subChecking || (isAuthenticated && permissionLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  /* -------------------------------------------------- */
  /* Role-Based Checks                                 */
  /* -------------------------------------------------- */
  if (requireRole && user?.role !== requireRole) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  /* -------------------------------------------------- */
  /* Subscription Enforcement (Admin Only)             */
  /* -------------------------------------------------- */
  if (user?.role === "admin" && subRequiresPayment) {
    return (
      <Navigate
        to="/subscription"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  /* -------------------------------------------------- */
  /* RBAC Enforcement                                  */
  /* -------------------------------------------------- */
  if (permission) {
    const features = toArray(permission.feature);
    const actions = toArray(permission.actions);

    let allowed = false;

    for (const feature of features) {
      for (const action of actions) {
        if (can(feature, action)) {
          allowed = true;
          break;
        }
      }
      if (allowed) break;
    }

    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  /* -------------------------------------------------- */
  /* All Checks Passed                                 */
  /* -------------------------------------------------- */
  return children;
};

export default ProtectedRoute;
